import { useState, useEffect, useMemo } from 'react';

import { getMMMData, getMMMRuns } from '../../api/mmmApi';
import { unixToReadableDate } from '../MarketingMixModeling/helpers';

/**
 * useModelRuns
 *
 * This hook manages model runs data and KPI type selection for the Model Comparison view.
 * It fetches all model runs via getMMMRuns(), sorts them by execution date, and determines
 * the most common KPI type across runs to set as default. It also creates a mapping of
 * model names to their formatted execution dates.
 *
 * @returns {Object} An object containing:
 *  - `modelRuns` {Array} - Model runs filtered by selected KPI type, sorted by date descending
 *  - `kpiType` {string} - Currently selected KPI type ('revenue' or 'conversions')
 *  - `setKpiType` {Function} - Updates the selected KPI type
 *  - `kpiTypeOptions` {Array} - Available KPI type options formatted for dropdown
 *                               Each option has: {key, text ('ROI'/'CPA'), value}
 *  - `execDtNameMap` {Object} - Maps model names to their formatted execution dates.
 *                               Format: { [modelName]: formattedExecDt }
 *                               If model has no name, uses formatted execution date as both key and value
 *                               Example: { "Model A": "2023-01-01", "2023-02-01": "2023-02-01" }
 */
export const useModelRuns = () => {
  const [allModelRuns, setAllModelRuns] = useState([]);
  const [kpiType, setKpiType] = useState('revenue');
  const [availableKpiTypes, setAvailableKpiTypes] = useState([]);
  const [execDtNameMap, setExecDtNameMap] = useState({});

  useEffect(() => {
    getMMMRuns().then(data => {
      const sortedRuns = [...data].sort((a, b) => b.exec_dt - a.exec_dt);
      setAllModelRuns(sortedRuns);

      const nameMap = sortedRuns.reduce((acc, run) => {
        const formattedDate = unixToReadableDate(run.exec_dt);
        const key = run.name || formattedDate;
        return {
          ...acc,
          [key]: formattedDate,
        };
      }, {});
      setExecDtNameMap(nameMap);

      const uniqueKpiTypes = [...new Set(data.map(run => run.kpi))];
      setAvailableKpiTypes(uniqueKpiTypes);

      const kpiCounts = data.reduce((counts, run) => ({
        ...counts,
        [run.kpi]: (counts[run.kpi] || 0) + 1,
      }), {});

      const mostCommonKpi = Object.entries(kpiCounts)
        .sort(([, a], [, b]) => b - a)[0][0];

      setKpiType(mostCommonKpi);
    });
  }, []);

  const filteredModelRuns = useMemo(() => (
    allModelRuns.filter(run => run.kpi === kpiType)
  ), [allModelRuns, kpiType]);

  const kpiTypeOptions = useMemo(() => (
    availableKpiTypes.map(type => ({
      key: type,
      text: type === 'conversions' ? 'CPA' : 'ROI',
      value: type,
    }))
  ), [availableKpiTypes]);

  return {
    modelRuns: filteredModelRuns,
    kpiType, setKpiType,
    kpiTypeOptions,
    execDtNameMap,
  };
};

/**
 * useModelsData
 *
 * This hook fetches and processes performance metrics for multiple model runs.
 * It retrieves data for each execution date from the `getMMMData` function and calculates
 * either ROI or CPA values depending on the KPI type.
 *
 * @param {Array} modelRuns - Array of model runs, each containing:
 *   - exec_dt {string} - Execution date identifier
 *   - name {string} - Model name
 * @param {string} kpiType - KPI type ('revenue' or 'conversions')
 *
 * @returns {Object}
 *   - data {Array|false} Array of objects containing:
 *     - execDt {string} - Execution date
 *     - name {string} - Model name
 *     - roiValues {Object} - Channel metrics where:
 *       - key: Channel name
 *       - value: ROI value for revenue KPI, or CPA value (1/ROI) for conversions KPI
 *     Returns false if there's an error fetching data
 */
export const useModelsData = (modelRuns, kpiType) => {
  const [data, setData] = useState(false);

  const execDtsModelNames = useMemo(() => (
    modelRuns.map(({ exec_dt, name }) => ({ execDt: exec_dt, name }))
  ), [modelRuns]);

  useEffect(() => {
    if (!execDtsModelNames.length) return;

    Promise.all(execDtsModelNames.map(({ execDt }) => getMMMData(execDt)))
      .then(response => {
        const metricValues = response.map((modelData, i) => {
          const { execDt, name } = execDtsModelNames[i];
          const roiValues = modelData.roi_mean.reduce((result, { rn, roi_mean }) => ({
            ...result,
            ...(rn.endsWith('_IMP') ? {} : {
              [rn]: kpiType === 'conversions' ? (roi_mean === 0 ? 0 : 1 / roi_mean) : roi_mean,
            }),
          }), {});

          return { execDt, name, roiValues };
        });
        setData(metricValues);
      })
      .catch(err => {
        console.error(`Failed to fetch MMM Model Data for exec dates ${execDtsModelNames}: ${err}`);
        setData(false);
      });
  }, [execDtsModelNames, kpiType]);

  return { data };
};

/**
 * useChartData
 *
 * Processes model performance data into a format suitable for visualization and assigns colors to each model.
 *
 * Key functionality:
 * 1. Remaps feature names using provided aliases
 * 2. Extracts unique features across all models
 * 3. Formats data for chart consumption with consistent structure
 * 4. Creates a color scheme - blue for first model, progressively lighter greys for others
 *
 * @param {Object[]} data - Model performance data, where each object contains:
 *   - execDt {string} - Model execution date
 *   - name {string} - Model name
 *   - roiValues {Object} - Channel performance metrics
 * @param {Object} featureAliases - Maps raw feature names to display names
 *
 * @returns {Object}
 *   - loading {boolean} - True if data is not yet ready
 *   - chartData {Object[]} - Formatted data for chart rendering, each object contains:
 *       - name {string} - Feature name
 *       - [modelName] {number} - Performance value for each model
 *   - execDtsColors {Object} - Color mapping for each model
 *       - key: Model name/date
 *       - value: Hex/RGB color value
 */
export const useChartData = (data, featureAliases) => {
  const remappedData = useMemo(() => {
    if (!data) return [];
    return data.map(obj => {
      const remapped = Object.entries(obj.roiValues).reduce((accu, [key, value]) => {
        const alias = featureAliases[key];
        if (alias) accu[alias] = value;
        return accu;
      }, {});
      return {
        ...obj,
        roiValues: remapped,
      };
    });
  }, [data, featureAliases]);

  const features = useMemo(() => {
    if (!remappedData.length) return [];
    const allFeatures = remappedData.flatMap(model => Object.keys(model.roiValues));
    return [...new Set(allFeatures)].sort();
  }, [remappedData]);

  const chartData = useMemo(() => {
    if (!data.length) return [];

    return features.map(feature => {
      const values = remappedData
        .reverse()
        .reduce((accu, curr) => {
          const { execDt, name, roiValues } = curr;
          const modelName = name || unixToReadableDate(execDt);
          accu[modelName] = roiValues[feature] || 0;
          return accu;
        }, {});

      return {
        name: feature,
        ...values,
      };
    });
  }, [features]);

  const execDtsColors = useMemo(() => {
    if (!data.length) return {};
    return data.reduce((accu, curr, index) => {
      const { execDt, name } = curr;
      const modelName = name || unixToReadableDate(execDt);
      accu[modelName] = index === 0
        ? '#475ddc'
        // Start with a darker grey and increase contrast between shades
        : `rgb(${Math.min(100 + index * 35, 220)}, ${Math.min(100 + index * 35, 220)}, ${Math.min(100 + index * 35, 220)})`;
      return accu;
    }, {});
  }, [data]);

  return {
    loading: !chartData.length,
    chartData,
    execDtsColors,
  };
};
